import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { screensApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatNumber, formatDuration } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business スクリーン一覧ページ。画面名検索・ソート対応テーブルでイベント数/ユーザー数/セッション数/滞在時間を表示
 * @graph-connects screensApi [calls] Screens APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDuration [calls] 時間フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/screens/')({
  component: ScreensPage,
});

// ============================================================
// ソート
// ============================================================

type SortKey = 'screen' | 'eventCount' | 'uniqueUsers' | 'uniqueSessions' | 'avgDwellTimeMs';

interface ScreenRow {
  screen: string;
  service: string;
  eventCount: number;
  uniqueUsers: number;
  uniqueSessions: number;
  avgDwellTimeMs: number | null;
  rawScreenCount: number;
}

/** @graph-connects none */
function sortScreens(data: ScreenRow[], sortKey: SortKey, ascending: boolean): ScreenRow[] {
  return [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    const cmp = (aVal as number) - (bVal as number);
    return ascending ? cmp : -cmp;
  });
}

// ============================================================
// テーブルヘッダー
// ============================================================

/** @graph-connects none */
function SortableHeader({
  label,
  sortKey,
  currentSort,
  ascending,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  ascending: boolean;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider cursor-pointer hover:text-[var(--color-text-primary)] select-none"
      onClick={() => {
        onSort(sortKey);
      }}
    >
      <span className="flex items-center gap-1">
        {label}
        {isActive && (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={ascending ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        )}
      </span>
    </th>
  );
}

// ============================================================
// メインページ
// ============================================================

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function ScreensPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('uniqueUsers');
  const [ascending, setAscending] = useState(false);

  const screensQuery = useQuery({
    queryKey: queryKeys.screens.list(startDate, endDate),
    queryFn: () => screensApi.getList(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setAscending(!ascending);
    } else {
      setSortKey(key);
      setAscending(false);
    }
  };

  const filteredData = useMemo(() => {
    const data = screensQuery.data?.data ?? [];
    const searchLower = search.toLowerCase();
    const filtered = searchLower
      ? data.filter((s) => s.screen.toLowerCase().includes(searchLower))
      : data;
    return sortScreens(filtered, sortKey, ascending);
  }, [screensQuery.data, search, sortKey, ascending]);

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('screens.title')}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">{t('screens.description')}</p>
        </div>
        <DateRangeSelector
          preset={preset}
          startDate={startDate}
          endDate={endDate}
          onPresetChange={setPreset}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* 検索 */}
      <div className="card !p-4">
        <input
          type="text"
          placeholder={t('screens.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          className="w-full px-3 py-2 rounded-lg bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] text-sm border border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none transition-colors"
        />
      </div>

      {/* テーブル */}
      <div className="card !p-0 overflow-hidden">
        {screensQuery.isLoading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-shimmer h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <SortableHeader
                    label={t('screens.screenName')}
                    sortKey="screen"
                    currentSort={sortKey}
                    ascending={ascending}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label={t('screens.totalEvents')}
                    sortKey="eventCount"
                    currentSort={sortKey}
                    ascending={ascending}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label={t('screens.uniqueUsers')}
                    sortKey="uniqueUsers"
                    currentSort={sortKey}
                    ascending={ascending}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label={t('screens.uniqueSessions')}
                    sortKey="uniqueSessions"
                    currentSort={sortKey}
                    ascending={ascending}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label={t('screens.avgDwellTime')}
                    sortKey="avgDwellTimeMs"
                    currentSort={sortKey}
                    ascending={ascending}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {filteredData.map((screen) => (
                  <tr
                    key={screen.screen}
                    className="hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-[var(--color-text-primary)]">
                      <span className="flex items-center gap-1.5">
                        {screen.screen}
                        {screen.rawScreenCount > 1 && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-accent-surface)] text-[var(--color-accent-primary)]"
                            title={`${String(screen.rawScreenCount)} URL variants grouped`}
                          >
                            {formatNumber(screen.rawScreenCount)} URLs
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)] tabular-nums">
                      {formatNumber(screen.eventCount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)] tabular-nums">
                      {screen.rawScreenCount > 1 ? '~' : ''}
                      {formatNumber(screen.uniqueUsers)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)] tabular-nums">
                      {screen.rawScreenCount > 1 ? '~' : ''}
                      {formatNumber(screen.uniqueSessions)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)] tabular-nums">
                      {screen.avgDwellTimeMs !== null ? formatDuration(screen.avgDwellTimeMs) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to="/screens/$screenName"
                        params={{ screenName: screen.screen }}
                        className="text-xs text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)] transition-colors"
                      >
                        {t('screens.viewDetail')} →
                      </Link>
                    </td>
                  </tr>
                ))}
                {filteredData.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]"
                    >
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
